import { useState, type ReactNode } from 'react';
import { Button } from '@/shared/components/ui/button';
import { Clock, ListChecks, Lock } from 'lucide-react';
import { stashAssessmentClaimToken } from '../hooks/useTakeAssessment';

const GREEN = 'bg-[#026857] hover:bg-[#025247]';

function CenteredCard({ children }: { children: ReactNode }) {
  return (
    <div className="mx-auto w-full max-w-xl rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-sm">
      {children}
    </div>
  );
}

/**
 * The screen before the first question. Its promises have to match the
 * assessment behind them, so the headline, the body and the three facts are
 * props rather than fixed copy — MVP 2 has five pillars, a discovery layer, and
 * sharing that is opt-out rather than private by default. The defaults are V1's
 * copy, which is still live and unchanged.
 */
export function LandingScreen({
  clientName,
  advisorName,
  onStart,
  headline = 'How ready are you for a long life?',
  body,
  scope = 'This assessment looks at how prepared you are across six important areas.',
  duration = '~15 minutes',
  questionCount = '30 questions',
  privacy = 'Private',
}: {
  clientName: string;
  advisorName: string;
  onStart: () => void;
  headline?: string;
  /** Replaces the whole intro when given; `scope` is then unused. */
  body?: string[];
  scope?: string;
  duration?: string;
  questionCount?: string;
  privacy?: string;
}) {
  return (
    <CenteredCard>
      <p className="text-xs font-semibold uppercase tracking-wide text-[#026857]">
        bQuest Longevity Readiness Assessment
      </p>
      <h1 className="mt-3 font-serif text-3xl text-gray-900">{headline}</h1>
      {body ? (
        body.map((paragraph) => (
          <p
            key={paragraph}
            className="mx-auto mt-4 max-w-md text-[15px] leading-relaxed text-gray-600"
          >
            {paragraph}
          </p>
        ))
      ) : (
        <p className="mx-auto mt-4 max-w-md text-[15px] leading-relaxed text-gray-600">
          Financial planning isn&apos;t only about retirement — it&apos;s about preparing for a long
          life. {scope}
        </p>
      )}

      <div className="mt-6 flex flex-wrap items-center justify-center gap-3 text-sm text-gray-500">
        <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4" /> {duration}</span>
        <span className="inline-flex items-center gap-1.5"><ListChecks className="h-4 w-4" /> {questionCount}</span>
        <span className="inline-flex items-center gap-1.5"><Lock className="h-4 w-4" /> {privacy}</span>
      </div>

      <Button type="button" className={`mt-8 ${GREEN}`} onClick={onStart}>
        Start assessment
      </Button>
      <p className="mt-4 text-xs text-gray-400">
        Prepared for {clientName} by {advisorName}
      </p>
    </CenteredCard>
  );
}

export function VisionScreen({
  initialValue,
  onContinue,
}: {
  initialValue: string;
  onContinue: (value: string) => void;
}) {
  const [value, setValue] = useState(initialValue);
  return (
    <CenteredCard>
      <h2 className="font-serif text-2xl text-gray-900">Your vision</h2>
      <p className="mx-auto mt-3 max-w-md text-[15px] leading-relaxed text-gray-600">
        In one or two sentences, describe what a successful later life looks like to you.
      </p>
      <p className="mt-2 text-sm text-gray-400">Optional — but it helps shape your report.</p>
      <textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        maxLength={2000}
        rows={4}
        className="mt-4 w-full rounded-lg border border-gray-200 p-3 text-left text-[15px] focus:border-[#026857] focus:outline-none"
        placeholder="A successful later life for me looks like…"
      />
      <div className="mt-6">
        <Button type="button" className={GREEN} onClick={() => onContinue(value.trim())}>
          Continue
        </Button>
      </div>
    </CenteredCard>
  );
}

export function ConcernScreen({
  initialValue,
  onFinish,
  isSaving,
}: {
  initialValue: string;
  onFinish: (value: string) => void;
  isSaving: boolean;
}) {
  const [value, setValue] = useState(initialValue);
  const canFinish = value.trim().length > 0;
  return (
    <CenteredCard>
      <h2 className="font-serif text-2xl text-gray-900">One last question</h2>
      <p className="mx-auto mt-3 max-w-md text-[15px] leading-relaxed text-gray-600">
        When you think about growing older, what feels most uncertain or concerning to you?
      </p>
      <textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        maxLength={2000}
        rows={4}
        className="mt-4 w-full rounded-lg border border-gray-200 p-3 text-left text-[15px] focus:border-[#026857] focus:outline-none"
        placeholder="What concerns you most…"
      />
      <div className="mt-6">
        <Button
          type="button"
          className={GREEN}
          disabled={!canFinish || isSaving}
          onClick={() => onFinish(value.trim())}
        >
          {isSaving ? 'Saving…' : 'Finish'}
        </Button>
      </div>
    </CenteredCard>
  );
}

export function ExpiredNotice() {
  return (
    <CenteredCard>
      <h2 className="font-serif text-2xl text-gray-900">This link has expired</h2>
      <p className="mx-auto mt-3 max-w-md text-[15px] leading-relaxed text-gray-600">
        Your assessment link is no longer active. Please ask your advisor to resend it.
      </p>
    </CenteredCard>
  );
}

export function NotFoundNotice() {
  return (
    <CenteredCard>
      <h2 className="font-serif text-2xl text-gray-900">Assessment not found</h2>
      <p className="mx-auto mt-3 max-w-md text-[15px] leading-relaxed text-gray-600">
        We couldn&apos;t find an assessment for this link. Please check the link from your advisor.
      </p>
    </CenteredCard>
  );
}

export function CompletionPlaceholder() {
  return (
    <CenteredCard>
      <h2 className="font-serif text-2xl text-gray-900">Thank you — you&apos;re all done</h2>
      <p className="mx-auto mt-3 max-w-md text-[15px] leading-relaxed text-gray-600">
        Your answers have been saved.
      </p>
    </CenteredCard>
  );
}

/**
 * Two-path account handoff (2026-09-11): existing accounts go to normal
 * sign-in, new users to account creation. `hasAccount` (email lookup on the
 * take payload) picks which path leads; the other is always one click away.
 * Both paths stash the claim token first — AuthCallback claims the assessment
 * after either sign-in or sign-up completes.
 */
export function AccountHandoffActions({
  token,
  clientEmail,
  hasAccount,
  onCreateAccount,
  onSignIn,
}: {
  token: string;
  clientEmail: string;
  hasAccount: boolean;
  onCreateAccount: (email: string) => void;
  onSignIn: () => void;
}) {
  const goCreate = () => {
    stashAssessmentClaimToken(token);
    onCreateAccount(clientEmail);
  };
  const goSignIn = () => {
    stashAssessmentClaimToken(token);
    onSignIn();
  };

  if (hasAccount) {
    return (
      <div className="mt-6">
        <Button type="button" className={GREEN} onClick={goSignIn}>
          Sign in &amp; view results
        </Button>
        <p className="mt-3 text-sm text-gray-500">
          New to bQuest?{' '}
          <button type="button" className="font-medium text-[#026857] underline" onClick={goCreate}>
            Create an account
          </button>
        </p>
      </div>
    );
  }

  return (
    <div className="mt-6">
      <Button type="button" className={GREEN} onClick={goCreate}>
        Create account &amp; view results
      </Button>
      <p className="mt-3 text-sm text-gray-500">
        Have an account?{' '}
        <button type="button" className="font-medium text-[#026857] underline" onClick={goSignIn}>
          Sign in
        </button>
      </p>
    </div>
  );
}

/**
 * Shown when an already-completed link is re-opened. Without this the client
 * would dead-end here, so offer the same handoff as the completion screen.
 */
export function CompletedReturnScreen({
  token,
  clientEmail,
  hasAccount,
  onCreateAccount,
  onSignIn,
}: {
  token: string;
  clientEmail: string;
  hasAccount: boolean;
  onCreateAccount: (email: string) => void;
  onSignIn: () => void;
}) {
  return (
    <CenteredCard>
      <h2 className="font-serif text-2xl text-gray-900">You&apos;ve already completed this</h2>
      <p className="mx-auto mt-3 max-w-md text-[15px] leading-relaxed text-gray-600">
        {hasAccount
          ? 'Your answers are saved. Sign in to view your full report.'
          : 'Your answers are saved. Create your account to view your full report.'}
      </p>
      <AccountHandoffActions
        token={token}
        clientEmail={clientEmail}
        hasAccount={hasAccount}
        onCreateAccount={onCreateAccount}
        onSignIn={onSignIn}
      />
    </CenteredCard>
  );
}
